import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { isSafeRedirect } from "@/lib/safeRedirect";
import { toast } from "sonner";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // Phase POLISH-1 #1 — show a banner + toast when the user was kicked
    // back here because their session naturally hit the cap or was
    // revoked server-side. Cleared after we render once so a manual
    // navigation to /login (e.g. via the "Sign in" link) is mute.
    const [sessionEndReason, setSessionEndReason] = useState(null);

    useEffect(() => {
        const e = searchParams.get("email");
        if (e) setEmail(e);
    }, [searchParams]);

    useEffect(() => {
        const reason = sessionStorage.getItem("session_end_reason");
        if (!reason) return;
        sessionStorage.removeItem("session_end_reason");
        setSessionEndReason(reason);
        if (reason === "max_lifetime") {
            toast.info("Your 24-hour session ended — sign in again to keep going.", {
                id: "session-end-max-lifetime",
            });
        } else if (reason === "revoked") {
            toast.warning("You were signed out. Please sign in again.", {
                id: "session-end-revoked",
            });
        }
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const u = await login(email, password, rememberMe);
            toast.success(`Welcome back, ${u.full_name}`);
            // Phase H.8 — must_reset_password always wins. Otherwise honor
            // a safe `redirect_to` (allowlist) before falling back to the
            // role-default landing page.
            const redirect = searchParams.get("redirect_to");
            const safe = redirect && isSafeRedirect(redirect) ? redirect : null;
            if (u.must_reset_password) navigate("/change-password", { replace: true });
            else if (safe) navigate(safe, { replace: true });
            else if (u.role === "customer") navigate("/customer", { replace: true });
            else if (u.role === "manager") navigate("/manager", { replace: true });
            else navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const signupHref = (() => {
        const sp = new URLSearchParams();
        const redirect = searchParams.get("redirect_to");
        if (redirect) sp.set("redirect_to", redirect);
        if (email) sp.set("email", email);
        const qs = sp.toString();
        return `/signup${qs ? `?${qs}` : ""}`;
    })();

    return (
        <AuthLayout
            eyebrow="Sign in"
            title="Welcome back."
            subtitle="Access your rental portal — review submissions, manage admins, or continue your application."
            footer={
                <>
                    Don't have an account?{" "}
                    <Link to={signupHref} data-testid="link-to-signup" className="font-medium text-blue-900 hover:underline">
                        Create a customer account
                    </Link>
                </>
            }
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                {sessionEndReason === "max_lifetime" && (
                    <div
                        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
                        data-testid="login-session-end-banner-max-lifetime"
                    >
                        Your 24-hour session ended — sign in again to keep going.
                    </div>
                )}
                {sessionEndReason === "revoked" && (
                    <div
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                        data-testid="login-session-end-banner-revoked"
                    >
                        You were signed out. Please sign in again.
                    </div>
                )}
                <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        data-testid="login-email-input"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                        id="password"
                        data-testid="login-password-input"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                    />
                    <div className="flex justify-end">
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot-password-link"
                            className="text-xs text-slate-500 hover:text-blue-900 hover:underline decoration-dotted"
                        >
                            Forgot password?
                        </Link>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="remember-me"
                        data-testid="login-remember-me-checkbox"
                        checked={rememberMe}
                        onCheckedChange={(v) => setRememberMe(v === true)}
                    />
                    <Label
                        htmlFor="remember-me"
                        className="text-sm text-slate-600 font-normal cursor-pointer select-none"
                    >
                        Keep me signed in for 30 days
                    </Label>
                </div>

                {error && (
                    <div
                        data-testid="login-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Signing in…" : "Sign in"}
                </Button>
            </form>
        </AuthLayout>
    );
}
